/**
 * **보는 사람이 향한 쪽을 더 보여 준다.** 평소에는 카메라가 보는 사람(장수, 또는 몰고 있는 부하장수)을 화면 한가운데에
 * 두었다 — 등 뒤의 빈 땅이 화면의 절반이고, 앞의 적은 절반만 보였다. 플레이한 사람이 청했다(2026-10-07): "확대되더라도
 * 현재 포커스 중인 장수가 향한 방향을 더 많이 보고 싶어." 폰에서는 싸우는 동안 1.5배로 당겨 보므로 앞이 더 좁다.
 *
 * 향한 쪽(`World.faceX`·`faceY`)으로 화면 길이의 `FACE_LOOK`만큼 볼 곳을 옮긴다. 향한 쪽은 싸우는 동안 표적을 따라 자주
 * 뒤집히므로 그대로 쓰면 화면이 출렁인다 — 천천히 따라가는 값(`FaceLook`)으로 누그러뜨린다.
 *
 * 화면(렌더링)만의 일이다 — 시뮬레이션은 읽기만 한다. 그림(Pixi)을 모르는 셈만 둔다(시험이 본다).
 */

/** 향한 쪽으로 옮기는 몫(화면 길이의 몫). 0.2면 보는 사람이 화면 가운데에서 뒤로 20% 물러난 자리(뒤 끝에서 30%)에 선다. */
export const FACE_LOOK = 0.2;
/**
 * 우리 진영 쪽(전장 아래 — 물러설 때)을 향할 때의 몫. 절반만 옮긴다: 가로 폰에서는 그쪽이 화면 오른쪽이라 0.2를 다 옮기면
 * 보는 사람이 함성 단추 뒤(화면 너비의 70%)에 섰다(QA). 물러서는 길에는 볼 것도 적다 — 적은 등 뒤에 있다.
 */
export const FACE_LOOK_BACK = 0.1;
/**
 * 향한 쪽을 따라가는 걸음(**화면 프레임마다** — 60fps 기준이다. 30fps 기기는 절반 속도로, 120fps는 두 배로 따라간다;
 * 따라가는 카메라의 다른 걸음(`FOLLOW_LERP`)도 프레임 기준이라 이것만 시간으로 고치지 않았다). 0.04면 60fps에서 뒤돌아선
 * 뒤 반쯤 따라가는 데 0.3초, 거의 다 따라가는 데 1초 남짓이다.
 */
export const FACE_LERP = 0.04;

/** 누그러뜨린 향한 쪽. 길이는 0~1이다 — 이리저리 뒤집히는 동안에는 짧아져 화면이 가운데로 돌아온다. */
export class FaceLook {
  private x = 0;
  private y = 0;

  /**
   * 보는 사람이 바뀌면 가운데에서 다시 시작한다 — 옛 사람의 방향에서 미끄러져 오지 않는다. 판을 열 때도 가운데다:
   * 막 선 사람은 아직 걸은 적이 없어 향한 쪽이 뜻이 없다(우리 편은 모두 오른쪽을 보고 선다 — `World.spawn`).
   */
  reset(): void {
    this.x = 0;
    this.y = 0;
  }

  /** 이번 프레임의 향한 쪽(`fx`·`fy`, 길이는 아무래도 좋다)을 받아 누그러뜨린 값을 돌려준다. 없으면(0·0) 가운데로 돌아간다. */
  step(fx: number, fy: number, lerp = FACE_LERP): { x: number; y: number } {
    const len = Math.hypot(fx, fy);
    const tx = len > 0 ? fx / len : 0;
    const ty = len > 0 ? fy / len : 0;
    this.x += (tx - this.x) * lerp;
    this.y += (ty - this.y) * lerp;
    return { x: this.x, y: this.y };
  }
}

/**
 * 볼 곳을 옮기는 양(전장 픽셀): 누그러뜨린 향한 쪽 × **그 길이** × 화면 길이 × 몫. 가로는 화면 너비, 세로는 화면 높이로
 * 재고, 아래(우리 진영 쪽)로는 `FACE_LOOK_BACK`만큼만 간다.
 *
 * 길이를 한 번 더 곱한다(길이의 제곱으로 간다): 한쪽으로 굳으면 길이가 1이라 그대로지만, 이리저리 뒤집히는 동안에는 길이가
 * 짧아 훨씬 덜 간다. 그대로 곱하기만 했더니 조이스틱을 0.3초마다 반대로 꺾을 때 누그러뜨린 값이 ±0.35를 오가 보는 사람이
 * 화면 높이의 37%를 출렁였다(QA — 카메라 이동이 평소의 네 배). 제곱이면 그 폭이 ±0.12쯤이다.
 */
export function faceShift(dir: { x: number; y: number }, viewW: number, viewH: number): { x: number; y: number } {
  const len = Math.min(1, Math.hypot(dir.x, dir.y));
  return { x: dir.x * len * viewW * FACE_LOOK, y: dir.y * len * viewH * (dir.y > 0 ? FACE_LOOK_BACK : FACE_LOOK) };
}
